import { CaretDownOutlined, CaretUpOutlined, FileTextOutlined } from "@ant-design/icons";
import { EVisionMetricTypes, EVisionReportTypes, IReportGeneratorConfig } from "@kuika/kuika-cl-model";
import { memo, useState } from "react";
import { Handle, Position, useReactFlow } from "reactflow";
import KuikaSvg from "../../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { NODE_TOOLTIPS } from "./nodesInfoContent";
import baseStyles from "./BaseNode.module.css";

interface ReportGeneratorNodeData {
  type: "ReportGenerator";
  title: string;
  config?: IReportGeneratorConfig;
  status?: "idle" | "generating" | "active";
}

interface ReportGeneratorNodeProps {
  data: ReportGeneratorNodeData;
  id: string;
  selected?: boolean;
}

const NODE_CATEGORY_STYLES = {
  output: {
    primaryColor: "#cf3112",
    headerBackground: "rgba(207, 49, 18, 0.1)",
    iconColor: "#cf3112",
    handleColor: "#cf3112"
  }
};

const ReportGeneratorNode = ({ data, id, selected }: ReportGeneratorNodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(data.status || "idle");
  const [config, setConfig] = useState<IReportGeneratorConfig>(() => {
    // Default config values matching backend VisionDataConfigModel
    const defaults: IReportGeneratorConfig = {
      reportType: EVisionReportTypes.Pdf,
      schedule: EVisionMetricTypes.Daily,
      recipients: [],
      includeCharts: true,
      includeHistoricalData: true
    };
    // Merge with provided config from template
    if (data.config) {
      return { ...defaults, ...data.config };
    }
    return defaults;
  });
  const { setNodes } = useReactFlow();

  const categoryStyles = NODE_CATEGORY_STYLES.output;

  const updateConfig = (updates: Partial<IReportGeneratorConfig>) => {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);

    setNodes((nodes) =>
      nodes.map((node) => (node.id === id ? { ...node, data: { ...node.data, config: newConfig } } : node))
    );
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes((nodes) => nodes.filter((node) => node.id !== id));
  };

  const renderInfoIcon = () => (
    <div className={baseStyles.nodeInfoTopRight}>
      <KuikaTooltip
        position="top"
        content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.reportGenerator.text}</div>}
      >
        <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
          <KuikaSvg name="info-circle" width={16} height={16} />
        </span>
      </KuikaTooltip>
    </div>
  );

  const renderSummary = () => {
    return (
      <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
        {renderInfoIcon()}
        <span className={baseStyles.collapsedSummary}>
          {config.reportType?.toUpperCase()} • {config.schedule}
        </span>
      </div>
    );
  };

  return (
    <div className={`${baseStyles.nodeContainer} ${selected ? "selected" : ""}`}>
      {selected && (
        <button
          className={`${baseStyles.deleteButton} ${baseStyles.nodrag}`}
          onClick={handleDelete}
          onMouseDown={(e) => e.stopPropagation()}
          title="Delete node"
        >
          <KuikaSvg type="common" name="x" width={14} height={14} color="var(--red-5)" />
        </button>
      )}
      <div
        className={`${baseStyles.nodeHeader} ${expanded ? baseStyles.nodeHeaderExpanded : ""}`}
        style={{ background: categoryStyles.headerBackground }}
      >
        <span className={baseStyles.nodeIcon} style={{ color: categoryStyles.iconColor }}>
          <FileTextOutlined />
        </span>
        <span className={baseStyles.nodeTitle}>
          {data.title}
          <KuikaTooltip
            position="top"
            content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.reportGenerator.text}</div>}
          >
            <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
              <KuikaSvg name="info-circle" width={16} height={16} />
            </span>
          </KuikaTooltip>
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          onMouseDown={(e) => e.stopPropagation()}
          className={`${baseStyles.expandButton} ${baseStyles.nodrag}`}
        >
          {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </button>
      </div>

      {!expanded && renderSummary()}

      {expanded && (
        <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
          {renderInfoIcon()}
          <div className={baseStyles.nodeForm}>
            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Report Type</label>
              <select
                className={`${baseStyles.formSelect} ${baseStyles.nodrag}`}
                value={config.reportType}
                onChange={(e) => updateConfig({ reportType: e.target.value as EVisionReportTypes })}
              >
                <option value={EVisionReportTypes.Pdf}>PDF</option>
                <option value={EVisionReportTypes.Excel}>Excel</option>
                <option value={EVisionReportTypes.Json}>JSON</option>
              </select>
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Schedule</label>
              <select
                className={`${baseStyles.formSelect} ${baseStyles.nodrag}`}
                value={config.schedule}
                onChange={(e) => updateConfig({ schedule: e.target.value as EVisionMetricTypes })}
              >
                <option value={EVisionMetricTypes.Hourly}>Hourly</option>
                <option value={EVisionMetricTypes.Daily}>Daily</option>
                <option value={EVisionMetricTypes.Weekly}>Weekly</option>
                <option value={EVisionMetricTypes.Monthly}>Monthly</option>
              </select>
            </div>

            <div className={baseStyles.formGroup}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px" }}>
                <input
                  type="checkbox"
                  checked={config.includeCharts || false}
                  onChange={(e) => updateConfig({ includeCharts: e.target.checked })}
                  className={baseStyles.nodrag}
                />
                Include Charts
              </label>
            </div>

            <div className={baseStyles.formGroup}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px" }}>
                <input
                  type="checkbox"
                  checked={config.includeHistoricalData || false}
                  onChange={(e) => updateConfig({ includeHistoricalData: e.target.checked })}
                  className={baseStyles.nodrag}
                />
                Include Historical Data
              </label>
            </div>

            <button
              className={`${baseStyles.formButton} ${baseStyles.nodrag}`}
              onClick={() => setStatus(status === "generating" ? "idle" : "generating")}
            >
              {status === "generating" ? "Stop" : "Generate"} Report
            </button>
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className={baseStyles.targetHandle}
        style={{ background: categoryStyles.handleColor }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className={baseStyles.sourceHandle}
        style={{ background: categoryStyles.handleColor }}
      />
    </div>
  );
};

export default memo(ReportGeneratorNode);
